import React, { useState, useEffect } from 'react';

const toMinutes = (t) => {
  if (!t || t === '--:--') return -1;
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
};

const PRAYER_ORDER = ['shacharit', 'mincha', 'maariv', 'other'];

const ZMAN_SHORT = {
  daySunset: 'השקיעה', dayNetz: 'הנץ', dayAlot: 'עלות השחר', dayChatzot: 'חצות',
  dayMinchaGedola: 'מנחה גדולה', dayMinchaKetana: 'מנחה קטנה', dayPlagHamincha: 'פלג המנחה',
  dayTzeit7083: 'צאת הכוכבים', dayTzeit85: 'צאת הכוכבים', dayTzeit42: 'צאת הכוכבים',
  dayTzeit50: 'צאת הכוכבים', dayTzeit72: 'צאת הכוכבים',
  daySofZmanShma: 'סוף זמן שמע', daySofZmanShmaMGA: 'סוף זמן שמע',
  daySofZmanTfilla: 'סוף זמן תפילה', daySofZmanTfillaMGA: 'סוף זמן תפילה',
};
const formatTimeRule = (ct) => {
  if (ct.type === 'fixed') return 'שעה קבועה';
  if (ct.type === 'relative') {
    const label = ZMAN_SHORT[ct.baseTime] || ct.baseTime;
    const offset = parseInt(ct.offset) || 0;
    if (offset === 0) return label;
    const abs = Math.abs(offset);
    return offset < 0 ? `${abs} דק׳ לפני ${label}` : `${abs} דק׳ אחרי ${label}`;
  }
  return null;
};

const CAT = {
  shacharit: { accent: '#1e3a5f', label: 'שחרית' },
  mincha:    { accent: '#b8860b', label: 'מנחה'  },
  maariv:    { accent: '#374151', label: 'מעריב' },
  other:     { accent: '#64748b', label: 'אחר'   },
};

const todayDow = new Date().getDay();
const todayHebrewDay = (() => {
  try { return parseInt(new Intl.DateTimeFormat('he-u-ca-hebrew', { day: 'numeric' }).format(new Date())); }
  catch { return 0; }
})();
const isRoshChodeshToday = todayHebrewDay === 1 || todayHebrewDay === 30;
const isOverrideActive = (days) =>
  (days || []).includes(todayDow) || (isRoshChodeshToday && (days || []).includes('roshChodesh'));

const DAY_NAMES = { 0: 'א׳', 1: 'ב׳', 2: 'ג׳', 3: 'ד׳', 4: 'ה׳', 5: 'ו׳', roshChodesh: 'ר״ח' };

const StatusBox = ({ msg, isError }) => (
  <div style={{ width: '100%', padding: '2rem', display: 'flex', alignItems: 'center', justifyContent: 'center', direction: 'rtl', fontSize: '1.125rem', color: isError ? '#c0392b' : '#94a3b8' }}>
    {msg}
  </div>
);

const Cards = ({ processedCustomTimes, loading, error, title = 'תפילות יום חול', showTitle = true, recurringDisplay = 'compact', highlightNextMinyan = false }) => {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(t);
  }, []);

  const sorted = (!loading && !error && processedCustomTimes?.length)
    ? [...processedCustomTimes].sort((a, b) =>
        PRAYER_ORDER.indexOf(a.prayer || 'other') - PRAYER_ORDER.indexOf(b.prayer || 'other')
      )
    : [];

  const nowMin = now.getHours() * 60 + now.getMinutes();
  let nextIdx = -1;
  if (highlightNextMinyan && sorted.length) {
    let bestMin = Infinity;
    sorted.forEach((ct, i) => {
      if (ct.isActiveToday === false) return;
      const activeOverride = (ct.resolvedOverrides || []).find(ov => isOverrideActive(ov.days));
      const times = ct.type === 'recurring'
        ? (activeOverride ? [toMinutes(activeOverride.resolvedTime)] : (ct.resolvedSlots || []).map(toMinutes).filter(m => m >= 0))
        : (() => { const m = toMinutes(activeOverride ? activeOverride.resolvedTime : ct.resolvedTime); return m >= 0 ? [m] : []; })();
      for (const m of times) {
        if (m >= nowMin && m < bestMin) { bestMin = m; nextIdx = i; }
      }
    });
  }

  return (
    <div style={{ width: '100%', height: '100%', direction: 'rtl', display: 'flex', flexDirection: 'column', backgroundColor: '#f8f9fb', boxSizing: 'border-box', overflow: 'hidden' }}>
      {showTitle && (
        <div style={{ padding: '0.875rem 1.5rem', borderBottom: '1px solid #e2e8f0', flexShrink: 0, backgroundColor: '#ffffff' }}>
          <span style={{ fontWeight: 700, fontSize: '1.25rem', color: '#1e3a5f', letterSpacing: '0.01em' }}>{title}</span>
        </div>
      )}

      <div style={{ flex: 1, overflowY: 'auto', padding: '0.625rem', boxSizing: 'border-box' }}>
        {loading && <StatusBox msg="טוען..." />}
        {!loading && error && <StatusBox msg={error} isError />}
        {!loading && !error && !processedCustomTimes?.length && <StatusBox msg="אין זמני תפילה" />}
        {sorted.length > 0 && (
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.5rem',
            alignContent: 'start',
          }}>
            {sorted.map((ct, idx) => {
              const cat = CAT[ct.prayer] || CAT.other;
              const label = ct.label || cat.label;
              const isRecurring = ct.type === 'recurring';
              const activeOverride = (ct.resolvedOverrides || []).find(ov => isOverrideActive(ov.days));
              const mainTime = activeOverride
                ? activeOverride.resolvedTime
                : isRecurring
                  ? (ct.resolvedSlots?.length ? `${ct.resolvedSlots[0]} – ${ct.resolvedSlots[ct.resolvedSlots.length - 1]}` : null)
                  : ct.resolvedTime;
              const isNext = highlightNextMinyan && idx === nextIdx;

              return (
                <div key={idx} style={{
                  flex: '1 1 140px',
                  minWidth: '130px',
                  maxWidth: '260px',
                  minHeight: '100px',
                  backgroundColor: isNext ? '#eff6ff' : '#ffffff',
                  borderRadius: '12px',
                  boxShadow: isNext ? `0 4px 16px rgba(30,58,95,0.18)` : '0 1px 4px rgba(0,0,0,0.07)',
                  border: isNext ? '2px solid #1e3a5f' : 'none',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                }}>
                  <div style={{ height: isNext ? '5px' : '4px', backgroundColor: cat.accent, flexShrink: 0 }} />
                  {isNext && (
                    <span style={{
                      display: 'block', textAlign: 'center', padding: '3px 0',
                      fontSize: '10px', fontWeight: 700, color: '#fff',
                      backgroundColor: '#1e3a5f',
                    }}>המניין הבא</span>
                  )}

                  <div style={{ padding: '0.75rem 1rem', display: 'flex', flexDirection: 'column', gap: '0.25rem', flex: 1 }}>
                    <span style={{ fontSize: '0.7rem', fontWeight: 700, color: cat.accent, letterSpacing: '0.08em', textTransform: 'uppercase', opacity: 0.85 }}>
                      {label}
                    </span>

                    {isRecurring && (
                      <span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#166534', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '99px', padding: '1px 7px', display: 'inline-block', width: 'fit-content' }}>
                        כל {ct.interval} דק׳
                      </span>
                    )}

                    <span style={{
                      fontSize: isRecurring ? '1.1rem' : '2rem',
                      fontWeight: 800,
                      color: activeOverride ? '#166534' : '#0f172a',
                      fontFamily: 'system-ui, -apple-system, sans-serif',
                      fontVariantNumeric: 'tabular-nums lining-nums',
                      direction: 'ltr',
                      lineHeight: 1.1,
                      letterSpacing: '-0.03em',
                    }}>
                      {mainTime || '—'}
                    </span>

                    {(!isRecurring && formatTimeRule(ct) || ct.daysLabel) && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0 4px', alignItems: 'baseline', marginTop: '2px' }}>
                        {!isRecurring && formatTimeRule(ct) && (
                          <span style={{ whiteSpace: 'nowrap', fontSize: '0.75rem', color: '#64748b', fontWeight: 500 }}>
                            {formatTimeRule(ct)}{ct.daysLabel ? ' ·' : ''}
                          </span>
                        )}
                        {ct.daysLabel && (
                          <span style={{ whiteSpace: 'nowrap', fontSize: '0.75rem', color: '#64748b', fontWeight: 500 }}>
                            {ct.daysLabel}
                          </span>
                        )}
                      </div>
                    )}

                    {isRecurring && recurringDisplay === 'full' && ct.resolvedSlots?.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', direction: 'ltr', marginTop: '4px' }}>
                        {ct.resolvedSlots.map((slot, i) => (
                          <span key={i} style={{ fontSize: '0.7rem', fontWeight: 600, color: '#475569', fontFamily: 'monospace', backgroundColor: '#f1f5f9', borderRadius: '4px', padding: '1px 5px' }}>
                            {slot}
                          </span>
                        ))}
                      </div>
                    )}

                    {(ct.resolvedOverrides || []).length > 0 && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', marginTop: '4px', paddingTop: '4px', borderTop: '1px solid #f1f5f9' }}>
                        {(ct.resolvedOverrides || []).map((ov, oi) => {
                          const active = isOverrideActive(ov.days);
                          const dayLabel = (ov.days || []).map(d => DAY_NAMES[d]).filter(Boolean).join(' ');
                          return (
                            <div key={oi} style={{
                              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                              padding: active ? '3px 6px' : '1px 2px',
                              borderRadius: '6px',
                              backgroundColor: active ? '#f0fdf4' : 'transparent',
                              border: active ? '1px solid #bbf7d0' : 'none',
                            }}>
                              <span style={{ fontSize: '0.7rem', color: active ? '#15803d' : '#cbd5e1', fontWeight: active ? 700 : 400 }}>{dayLabel}</span>
                              <span style={{ fontSize: '0.85rem', fontWeight: 700, color: active ? '#15803d' : '#cbd5e1', fontVariantNumeric: 'tabular-nums', direction: 'ltr' }}>{ov.resolvedTime}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Cards;
